// src/tabs/logements/ServiceBookingsPanel.jsx — Phase A.7.2b PR1 admin (ADR-122)
//
// Sub-onglet "Réservations" : liste paginée bookings + filtres + actions admin.
// C6 fonctionnel complet.
//
// Statuts gérés (6) :
//   pending_payment, pending_validation, confirmed,
//   non_delivered, refunded, cancelled.

import { useState, useEffect, useCallback } from "react";
import { DS } from "../../lib/ui";
import { callServicesAction } from "../../lib/api-services-client";

const STATUS_META = {
  pending_payment: { label: "En attente paiement", bg: "#f1f5f9", color: "#475569" },
  pending_validation: { label: "À valider", bg: "#fef3c7", color: "#ca8a04" },
  confirmed: { label: "Confirmé", bg: "#dcfce7", color: DS.green },
  non_delivered: { label: "Non livré", bg: "#fee2e2", color: DS.charge },
  refunded: { label: "Remboursé", bg: "#f1f5f9", color: "#475569" },
  cancelled: { label: "Annulé", bg: "#f1f5f9", color: "#475569" },
  // Demande hors plafond (> 50 km / > 50 €) ou adresse trop imprécise : rien
  // n'est payé, rien n'est réservé, elle attend une décision de prix.
  pending_manual_review: {
    label: "À valider (hors plafond)",
    bg: "#ffedd5",
    color: "#c2410c",
  },
};

const STATUS_OPTIONS = [
  { value: "", label: "Tous statuts" },
  { value: "pending_manual_review", label: "À valider (hors plafond)" },
  { value: "pending_validation", label: "À valider" },
  { value: "confirmed", label: "Confirmés" },
  { value: "pending_payment", label: "En attente paiement" },
  { value: "non_delivered", label: "Non livrés" },
  { value: "refunded", label: "Remboursés" },
  { value: "cancelled", label: "Annulés" },
];

function fmtPriceEur(cents) {
  if (typeof cents !== "number") return "—";
  return `${(cents / 100).toFixed(2)} €`;
}

function fmtDateShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function ServiceBookingsPanel({ logement }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [actionInProgress, setActionInProgress] = useState(null); // booking_id en cours
  const [confirmAction, setConfirmAction] = useState(null); // { bookingId, action }

  const load = useCallback(async () => {
    if (!logement?.id) return;
    setLoading(true);
    setError(null);
    try {
      const query = { logement_id: logement.id, limit: 100 };
      if (statusFilter) query.status = statusFilter;
      const res = await callServicesAction(
        "services_bookings_list",
        {},
        { method: "GET", query },
      );
      setBookings(res.bookings || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [logement?.id, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAction = async (bookingId, action) => {
    setActionInProgress(bookingId);
    setError(null);
    try {
      if (action === "validate") {
        await callServicesAction("services_admin_validate", {
          booking_id: bookingId,
        });
      } else if (action === "refund") {
        await callServicesAction("services_admin_refund", {
          booking_id: bookingId,
          reason: "admin_refund",
        });
      } else if (action === "non_delivered") {
        await callServicesAction("services_booking_mark_non_delivered", {
          booking_id: bookingId,
        });
      } else if (action === "manual_accept") {
        // CHEMIN ARGENT : on renvoie le montant EXACTEMENT tel qu'il est
        // affiché sur la ligne. Le serveur le recalcule et refuse (409
        // amount_changed) s'il a bougé depuis le chargement de la liste —
        // jamais d'encaissement silencieux à un autre montant.
        const b = bookings.find((x) => x.id === bookingId);
        await callServicesAction("services_manual_review_accept", {
          booking_id: bookingId,
          expected_amount_eur_cents: b?.amount_eur_cents,
        });
      } else if (action === "manual_reject") {
        await callServicesAction("services_manual_review_reject", {
          booking_id: bookingId,
        });
      }
      setConfirmAction(null);
      await load();
    } catch (e) {
      setError(e.message || `Action ${action} échouée.`);
    } finally {
      setActionInProgress(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: DS.textLight }}>
        Chargement réservations…
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 14,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontFamily: DS.fontDisplay,
            fontSize: 18,
            color: DS.navy,
          }}
        >
          Réservations services ({bookings.length})
        </h3>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: "8px 12px",
            border: `1px solid ${DS.border}`,
            borderRadius: 7,
            fontSize: 13,
            fontFamily: DS.fontBody,
            background: DS.white,
            cursor: "pointer",
          }}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div
          style={{
            padding: 12,
            background: "#fee2e2",
            color: DS.charge,
            borderRadius: 8,
            fontSize: 13,
            marginBottom: 14,
          }}
        >
          {error}
        </div>
      )}

      {bookings.length === 0 ? (
        <div
          style={{
            padding: 32,
            textAlign: "center",
            background: DS.white,
            borderRadius: 12,
            border: `1px dashed ${DS.border}`,
            color: DS.textLight,
            fontSize: 14,
          }}
        >
          Aucune réservation pour ce logement{statusFilter ? " avec ce statut" : ""}.
        </div>
      ) : (
        <div
          style={{
            background: DS.white,
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,.06)",
            overflow: "auto",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead style={{ background: DS.bg }}>
              <tr>
                <th style={th()}>Créée</th>
                <th style={th()}>Service</th>
                <th style={th()}>Voyageur</th>
                <th style={th()}>Dates</th>
                <th style={th(true)}>Montant</th>
                <th style={th()}>Statut</th>
                <th style={th()}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => {
                const meta = STATUS_META[b.status] || { label: b.status, bg: "#eee", color: "#555" };
                const inProgress = actionInProgress === b.id;
                return (
                  <tr key={b.id} style={{ borderTop: `1px solid ${DS.border}` }}>
                    <td style={td()}>{fmtDateShort(b.created_at)}</td>
                    <td style={td()}>
                      <code style={{ fontSize: 11 }}>{b.service_catalog_id.slice(0, 8)}</code>
                    </td>
                    <td style={td()}>
                      <div style={{ fontWeight: 600 }}>
                        {b.voyageur_data?.name || "—"}
                      </div>
                      <div style={{ fontSize: 11, color: DS.textLight }}>
                        {b.voyageur_data?.email || ""}
                      </div>
                    </td>
                    <td style={td()}>
                      {fmtDateShort(b.date_start)} → {fmtDateShort(b.date_end)}
                    </td>
                    <td style={td(true)}>
                      <strong>{fmtPriceEur(b.amount_eur_cents)}</strong>
                    </td>
                    <td style={td()}>
                      <span
                        style={{
                          background: meta.bg,
                          color: meta.color,
                          padding: "3px 8px",
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {meta.label}
                      </span>
                    </td>
                    <td style={td()}>
                      {confirmAction?.bookingId === b.id ? (
                        <ConfirmActionInline
                          action={confirmAction.action}
                          loading={inProgress}
                          onConfirm={() => handleAction(b.id, confirmAction.action)}
                          onCancel={() => setConfirmAction(null)}
                        />
                      ) : (
                        <RowActions
                          booking={b}
                          disabled={inProgress}
                          onAsk={(action) => setConfirmAction({ bookingId: b.id, action })}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RowActions({ booking, disabled, onAsk }) {
  // pending_validation → Valider (capture) + Refuser (refund)
  // confirmed → Non livré + remboursé
  // autres → pas d'action
  if (booking.status === "pending_validation") {
    return (
      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          onClick={() => onAsk("validate")}
          disabled={disabled}
          style={btn(DS.green)}
        >
          Valider
        </button>
        <button
          type="button"
          onClick={() => onAsk("refund")}
          disabled={disabled}
          style={btn(DS.charge, true)}
        >
          Refuser
        </button>
      </div>
    );
  }
  if (booking.status === "confirmed") {
    return (
      <button
        type="button"
        onClick={() => onAsk("non_delivered")}
        disabled={disabled}
        style={btn(DS.charge, true)}
      >
        Non livré + remb.
      </button>
    );
  }
  // Demande hors plafond : Dimitri tranche. « Accepter » émet un lien de
  // paiement au montant affiché ici même ; « Refuser » clôt la demande.
  if (booking.status === "pending_manual_review") {
    // Une demande refusée faute d'adresse exploitable n'a AUCUN kilométrage,
    // donc aucun montant : il n'y a rien à accepter tant que l'adresse n'est
    // pas connue. On le dit plutôt que d'afficher un bouton qui échouerait.
    const sansMontant =
      booking.manual_review_reason === "address_not_precise" ||
      !booking.amount_eur_cents;
    return (
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        {sansMontant ? (
          <span
            style={{ fontSize: 11, color: DS.textLight }}
            data-testid="svc-admin-no-amount"
          >
            Adresse à préciser
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onAsk("manual_accept")}
            disabled={disabled}
            data-testid="svc-admin-accept"
            style={btn(DS.green)}
          >
            {`Accepter à ${(booking.amount_eur_cents / 100)
              .toFixed(2)
              .replace(".", ",")} €`}
          </button>
        )}
        <button
          type="button"
          onClick={() => onAsk("manual_reject")}
          disabled={disabled}
          data-testid="svc-admin-reject"
          style={btn(DS.charge, true)}
        >
          Refuser
        </button>
      </div>
    );
  }
  return <span style={{ color: DS.textLight, fontSize: 11 }}>—</span>;
}

function ConfirmActionInline({ action, loading, onConfirm, onCancel }) {
  const label =
    action === "validate"
      ? "Capturer paiement ?"
      : action === "refund"
        ? "Rembourser ?"
        : action === "manual_accept"
          ? "Envoyer le lien de paiement à ce montant ?"
          : action === "manual_reject"
            ? "Refuser la demande ?"
            : "Marquer non livré + rembourser ?";
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      <span style={{ fontSize: 11, color: DS.charge, fontWeight: 600 }}>
        {label}
      </span>
      <button
        type="button"
        onClick={onConfirm}
        disabled={loading}
        style={btn(DS.navy)}
      >
        {loading ? "..." : "Oui"}
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={loading}
        style={btn("#94a3b8", true)}
      >
        Non
      </button>
    </div>
  );
}

function th(numeric = false) {
  return {
    padding: "10px 14px",
    textAlign: numeric ? "right" : "left",
    fontSize: 11,
    fontWeight: 700,
    color: DS.textLight,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    whiteSpace: "nowrap",
  };
}

function td(numeric = false) {
  return {
    padding: "12px 14px",
    textAlign: numeric ? "right" : "left",
    color: "#333",
    verticalAlign: "middle",
  };
}

function btn(color, outline = false) {
  return {
    padding: "5px 10px",
    border: outline ? `1px solid ${color}` : "none",
    borderRadius: 6,
    background: outline ? "transparent" : color,
    color: outline ? color : "#fff",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 600,
    fontFamily: DS.fontBody,
  };
}
